// Main menu landing page with dark fantasy atmospheric background.
// Fully responsive: the title scales with the viewport (clamp), and the
// hero art grid (.menu-hero in globals.css) switches layout by
// orientation and sizes itself to fit both the width and the height left
// after the title and buttons. Title, art and both CTAs must be on screen
// without scrolling from 320×568 up to 2560×1440; the page still scrolls
// as a fallback on very short viewports. scripts/audit/ui-invariants.mjs
// (menu-responsive) checks this at 7 sizes.

import { Button } from '@aod/ui/components/ui/button';
import { appDom } from '../lib/appDom.js';

interface Props {
  onStartSingle: () => void;
  /** Newest autosave, offered as "Continue" when present. */
  continueSave?: { readonly label: string; readonly savedAt: number } | null;
  onContinue?: () => void;
  onStartMultiplayer: () => void;
}

const HERO_IMAGES = [
  {
    src: '/assets/images/ui/menu_hero.webp',
    alt: 'Aged stone cities ringed in ley-line fire across a vast hex map',
  },
  {
    src: '/assets/images/ui/menu_hero_2.webp',
    alt: 'A frozen mountain fortress under an aurora-bathed sky',
  },
  {
    src: '/assets/images/ui/menu_hero_3.webp',
    alt: 'An ancient druidic forest with rune-marked temples',
  },
  {
    src: '/assets/images/ui/menu_hero_4.webp',
    alt: 'A coastal naval region with galleons, lighthouses and fortress ports',
  },
] as const;

/** "just now", "5 min ago", "3 h ago", "2 days ago". */
export function formatAgo(savedAt: number, now = Date.now()): string {
  const min = Math.max(0, Math.round((now - savedAt) / 60_000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export function MenuScreen({ onStartSingle, onStartMultiplayer, continueSave, onContinue }: Props) {
  return (
    <div
      data-testid="menu-page"
      className="relative h-full w-full overflow-x-hidden overflow-y-auto"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 30% 20%, #1a2450 0%, #0d0f1c 45%, #060810 100%)',
      }}
    >
      {/* Animated atmospheric glow orbs (CSS-only) */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute rounded-full opacity-20 animate-pulse"
          style={{
            width: 600,
            height: 600,
            left: '10%',
            top: '15%',
            background: 'radial-gradient(circle, rgba(180,140,80,0.4) 0%, transparent 70%)',
            animationDuration: '6s',
          }}
        />
        <div
          className="absolute rounded-full opacity-15"
          style={{
            width: 500,
            height: 500,
            right: '5%',
            bottom: '10%',
            background: 'radial-gradient(circle, rgba(100,140,220,0.35) 0%, transparent 70%)',
            animation: 'pulse 7s ease-in-out infinite',
          }}
        />
      </div>

      {/* Subtle hex grid texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='52' viewBox='0 0 60 52' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15 0L0 8.66v17.32L15 34.64l15-8.66V8.66L15 0zM45 0L30 8.66v17.32L45 34.64l15-8.66V8.66L45 0zM15 17.32L0 25.98v17.32L15 51.96l15-8.66V25.98L15 17.32zM45 17.32L30 25.98v17.32L45 51.96l15-8.66V25.98L45 17.32z' fill='none' stroke='rgba(255,255,255,0.04)' stroke-width='1.5'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 52px',
          opacity: 0.6,
        }}
      />

      <main
        data-testid="menu-screen"
        {...appDom({ id: 'menu-screen', type: 'Screen', scope: 'main', label: 'Main menu' })}
        className="menu-main relative z-10 mx-auto flex min-h-full w-full max-w-[1800px] flex-col items-center justify-center gap-[clamp(0.75rem,2.5dvh,1.75rem)] px-4 pt-[clamp(0.5rem,1.5dvh,1rem)] pb-[clamp(0.75rem,3dvh,2rem)]"
      >
        {/* Title area — fluid type, scales with the smaller viewport side */}
        <header className="menu-title-area text-center">
          <h1
            data-testid="menu-title"
            className="font-extrabold leading-[1.05] tracking-wide"
            style={{
              fontSize: 'clamp(2rem, min(7vw, 8dvh), 7rem)',
              background: 'linear-gradient(180deg, #e8d5a0 0%, #c49b4c 50%, #8b6914 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 80px rgba(180,140,80,0.3)',
            }}
          >
            Ages of Dominion
          </h1>
          <p
            className="mt-2 uppercase tracking-[0.3em] text-amber-200/60"
            style={{ fontSize: 'clamp(0.65rem, min(1.6vw, 1.8dvh), 1.35rem)' }}
          >
            Strategy &middot; Conquest &middot; Legacy
          </p>
        </header>

        {/* Hero art — one grid, laid out per orientation in globals.css
            (.menu-hero): 1 image on phones, 2×2 in portrait, 2 across on
            small landscape screens, a 4-strip on wide ones. Its width is
            the largest that fits both the viewport width and the height
            left after the title and buttons. */}
        <div
          data-testid="menu-hero"
          className={`menu-hero relative rounded-2xl p-[3px] shadow-2xl${continueSave && onContinue ? ' has-continue' : ''}`}
          style={{
            background:
              'linear-gradient(135deg, #f4d28a 0%, #c49b4c 25%, #8b6914 50%, #c49b4c 75%, #f4d28a 100%)',
            boxShadow:
              '0 0 80px rgba(196,155,76,0.35), 0 0 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
          }}
        >
          <div
            className="menu-hero-grid overflow-hidden rounded-[14px] border border-amber-100/20"
            style={{ background: 'linear-gradient(180deg, #0d0f1c 0%, #060810 100%)' }}
          >
            {HERO_IMAGES.map((img) => (
              <img
                key={img.src}
                src={img.src}
                alt={img.alt}
                className="block h-full w-full object-cover"
                loading="eager"
                draggable={false}
              />
            ))}
          </div>
        </div>

        {/* CTAs */}
        {/* The start-game hint anchors to the whole CTA group, so it opens
            below "or play multiplayer" instead of covering it. */}
        <div
          data-coachmark-target="start-game"
          data-testid="menu-ctas"
          className="menu-cta-area flex flex-col items-center gap-[clamp(0.4rem,1.2dvh,0.75rem)]"
        >
          <Button
            type="button"
            size="lg"
            onClick={onStartSingle}
            data-testid="start-game"
            {...appDom({
              id: 'start-game',
              type: 'Button',
              role: 'cta',
              scope: 'hero',
              label: 'Start single-player game',
            })}
            className="group relative h-auto overflow-hidden rounded-lg px-[clamp(2.5rem,5vw,6rem)] py-[clamp(0.75rem,2dvh,1.6rem)] text-[clamp(1rem,min(1.6vw,2.4dvh),1.9rem)] font-bold uppercase tracking-[0.2em] duration-200 hover:scale-[1.03] active:scale-[0.98]"
            style={{
              background: 'linear-gradient(135deg, #f4d28a 0%, #c49b4c 50%, #8b6914 100%)',
              color: '#1a0f02',
              boxShadow:
                '0 4px 30px rgba(196,155,76,0.4), inset 0 1px 0 rgba(255,255,255,0.4), inset 0 -2px 0 rgba(0,0,0,0.2)',
            }}
          >
            <span
              className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full"
              aria-hidden
            />
            <span className="relative">Start Game</span>
          </Button>
          {continueSave && onContinue && (
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={onContinue}
              data-testid="continue-game"
              {...appDom({
                id: 'continue-game',
                type: 'Button',
                role: 'cta',
                scope: 'hero',
                label: 'Continue the last autosaved game',
              })}
              className="h-auto flex-col gap-0.5 rounded-lg border-amber-300/40 bg-black/30 px-10 py-2.5 text-amber-100 hover:bg-black/50 hover:text-amber-50"
            >
              <span className="text-sm font-semibold uppercase tracking-[0.2em]">Continue</span>
              <span
                className="text-[11px] font-normal text-amber-100/60"
                data-testid="continue-game-meta"
              >
                {continueSave.label} · saved {formatAgo(continueSave.savedAt)}
              </span>
            </Button>
          )}
          <Button
            type="button"
            variant="link"
            onClick={onStartMultiplayer}
            data-testid="start-multiplayer"
            {...appDom({
              id: 'start-multiplayer',
              type: 'Button',
              role: 'cta',
              scope: 'hero',
              label: 'Play multiplayer',
            })}
            className="h-auto text-[clamp(0.8rem,min(1vw,1.6dvh),1.2rem)] font-normal uppercase tracking-[0.2em] text-amber-200/60 no-underline hover:text-amber-200 hover:no-underline"
          >
            or play multiplayer
          </Button>
        </div>

        {/* Version / footer hint */}
        <p className="text-[10px] text-foreground/30 md:text-[11px]">v0.1.0 &mdash; pre-alpha</p>
      </main>
    </div>
  );
}
